import useApiRequest from "@/lib/hooks/use-request";
import { PersonsPreviewResponseDto } from "@/lib/interfaces/organisations";
import { PersonListResponseDto } from "@/lib/interfaces/persons";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import LinkedItemManager from "@/ui-components/ui/linked-item-manager";
import { useCallback } from "react";
import PersonEditor from "./persons/person-editor";

type PersonLinkedProps = {
  onAssign: (userId: string, title: string) => Promise<void>;
  pipelineId?: string;
} & (
  | {
      multiple: true;
      item: PersonsPreviewResponseDto[];
      onRemove: (id: string) => Promise<void>;
    }
  | {
      multiple?: false;
      item?: PersonsPreviewResponseDto;
      onRemove?: () => Promise<void>;
    }
);

const PersonLinked = ({ item, onAssign, onRemove, multiple = false, pipelineId }: PersonLinkedProps) => {
  const { apiRequest } = useApiRequest();

  const fetchSearchResults = useCallback(
    async (pagination: Pagination, searchText: string): Promise<Paginated<PersonListResponseDto>> => {
      try {
        let url = `contacts/persons/search${pipelineId ? `/${pipelineId}` : ""}?limit=${pagination.pageSize}&page=${
          pagination.pageIndex + 1
        }`;
        if (searchText) {
          url = `${url}&search=${searchText}`;
        }
        const response = await apiRequest<Paginated<PersonListResponseDto>>(url, "GET");

        if (!response.data) {
          return {
            docs: [],
            hasNextPage: false,
            limit: pagination.pageSize,
            offset: pagination.pageIndex * pagination.pageSize,
            page: pagination.pageIndex,
            hasPrevPage: pagination.pageIndex > 0,
            count: 0,
            pageCount: 0,
          };
        }

        return response.data;
      } catch (error) {
        console.error("Error fetching persons:", error);
        return {
          docs: [],
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: 0,
          pageCount: 0,
        };
      }
    },
    [apiRequest, pipelineId]
  );

  const renderSearchOption = useCallback(
    (person: PersonListResponseDto) => ({
      label: `${person.firstName} ${person.lastName}`,
      value: person._id,
    }),
    []
  );

  if (multiple) {
    return (
      <>
        {Array.isArray(item) &&
          item.length > 0 &&
          item.map((person) => (
            <div key={person._id} className="py-1 border-b last:border-b-0">
              <LinkedItemManager<PersonListResponseDto>
                editor={<PersonEditor withoutRedirect={true} belongsToPipelineId={pipelineId} />}
                fetchSearchResults={fetchSearchResults}
                renderSearchOption={renderSearchOption}
                customOnSubmit={onAssign}
                removeItem={onRemove}
                link={{
                  title: person.fullName,
                  href: `/contacts/persons/${person._id}`,
                  id: person._id,
                }}
                title="Person"
              />
            </div>
          ))}
        <div>
          <LinkedItemManager<PersonListResponseDto>
            editor={<PersonEditor withoutRedirect={true} belongsToPipelineId={pipelineId} />}
            fetchSearchResults={fetchSearchResults}
            renderSearchOption={renderSearchOption}
            customOnSubmit={onAssign}
            removeItem={onRemove}
            title="Person"
            multiple={true}
          />
        </div>
      </>
    );
  }

  return (
    <LinkedItemManager<PersonListResponseDto>
      editor={<PersonEditor withoutRedirect={true} belongsToPipelineId={pipelineId} />}
      fetchSearchResults={fetchSearchResults}
      renderSearchOption={renderSearchOption}
      customOnSubmit={onAssign}
      removeItem={onRemove}
      link={
        item && !Array.isArray(item)
          ? {
              title: `${item.fullName}`,
              href: `/contacts/persons/${item._id}`,
              id: item._id,
            }
          : undefined
      }
      title="Person"
    />
  );
};

export default PersonLinked;
